import React, { useId } from "react"

import {
  GroupLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { FIELD_CONTAINER_STYLES, LABEL_STYLES } from "../styles"
import { RequiredMarker } from "./RequiredMarker"

const VALID_OPERATORS = [
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "==", label: "==" },
  { value: "!=", label: "!=" },
] as const

interface WordsPerMessageWithThresholdConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

export const WordsPerMessageWithThresholdConfig: React.FC<
  WordsPerMessageWithThresholdConfigProps
> = ({ metricRecordData, onChange }) => {
  const wordCountLabelId = useId()
  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        threshold: Number.isNaN(value) ? undefined : value,
      },
    })
  }

  const handleOperatorChange = (
    value: (typeof VALID_OPERATORS)[number]["value"],
  ) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        operator: value,
      },
    })
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div
        role="group"
        aria-labelledby={wordCountLabelId}
        className="flex flex-wrap items-center gap-x-2 gap-y-1"
      >
        <GroupLabel
          id={wordCountLabelId}
          className={`${LABEL_STYLES} shrink-0`}
        >
          Word count
          <RequiredMarker />
        </GroupLabel>
        <Select
          value={metricRecordData.metric_metadata?.operator ?? ""}
          onValueChange={(value) =>
            handleOperatorChange(
              value as (typeof VALID_OPERATORS)[number]["value"],
            )
          }
        >
          <SelectTrigger aria-label="Operator" className="w-16 shrink-0">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {VALID_OPERATORS.map((op) => (
              <SelectItem key={op.value} value={op.value}>
                {op.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="number"
          min={0}
          step={1}
          className="w-24 shrink-0"
          value={metricRecordData.metric_metadata?.threshold ?? ""}
          onChange={handleThresholdChange}
          placeholder="e.g. 10"
          aria-label="Threshold"
        />
        <span className="text-xs text-coval-text-muted min-w-0">
          (YES if all agent messages pass)
        </span>
      </div>
    </div>
  )
}
